import type { Festival, FestivalDetails } from "@workspace/types";

/**
 * Utility functions for handling festival status
 */

/**
 * Check if a festival has been cancelled
 */
export function isCancelledFestival(
	festival: Festival | FestivalDetails,
): boolean {
	return festival.is_cancelled === true;
}

/**
 * Check if a festival is lineup-only (has announced lineup but no schedule)
 */
export function isLineupOnlyFestival(
	festival: Festival | FestivalDetails,
): boolean {
	return festival.schedule_status === "lineup_only";
}

/**
 * Check if a festival has a complete schedule
 */
export function isCompleteFestival(
	festival: Festival | FestivalDetails,
): boolean {
	return festival.schedule_status === "complete";
}

/**
 * Get display text for festival status
 */
export function getFestivalStatusDisplay(
	status: "lineup_only" | "complete" | undefined,
): string {
	switch (status) {
		case "lineup_only":
			return "Lineup Only";
		case "complete":
			return "Complete Schedule";
		default:
			return "Unknown Status";
	}
}

/**
 * Get badge variant for festival status
 */
export function getFestivalStatusBadgeVariant(
	status: "lineup_only" | "complete" | undefined,
): {
	className: string;
	text: string;
} {
	switch (status) {
		case "lineup_only":
			return {
				className:
					"bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900 dark:text-amber-200 dark:border-amber-800",
				text: "Lineup Only",
			};
		case "complete":
			return {
				className:
					"bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200 dark:border-green-800",
				text: "Schedule Available",
			};
		default:
			return {
				className:
					"bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-800",
				text: "Unknown",
			};
	}
}

/**
 * Determine which tabs should be shown for a festival based on its status
 */
export function getFestivalTabConfig(festival: Festival | FestivalDetails): {
	showSchedule: boolean;
	showArtists: boolean;
	showReviews: boolean;
	showAttendees: boolean;
	defaultTab: "schedule" | "artists" | "reviews" | "attendees";
} {
	const isLineupOnly = isLineupOnlyFestival(festival);

	return {
		showSchedule: !isLineupOnly, // Hide schedule tab for lineup-only festivals
		showArtists: true, // Always show artists tab
		showReviews: true, // Always show reviews tab
		showAttendees: true, // Always show attendees tab
		defaultTab: isLineupOnly ? "artists" : "schedule", // Default to artists for lineup-only
	};
}

/**
 * Check if a lineup-only festival can transition to complete
 * This is a client-side check - the actual transition happens via database trigger
 */
export function canTransitionToComplete(
	hasStages: boolean,
	allSetsHaveSchedule: boolean,
): boolean {
	return hasStages && allSetsHaveSchedule;
}

/**
 * Get placeholder text for lineup-only festival schedule elements
 */
export function getLineupOnlyPlaceholders(): {
	stage: string;
	time: string;
	scheduleMessage: string;
} {
	return {
		stage: "Stage: TBA",
		time: "?????",
		scheduleMessage: "Schedule will be announced closer to the festival date.",
	};
}

/**
 * Filter festivals by schedule status for tab display
 */
export function filterFestivalsByStatus(
	festivals: Festival[],
	status: "all" | "upcoming" | "lineup" | "past",
): Festival[] {
	const now = new Date();
	now.setHours(0, 0, 0, 0);

	const isUpcomingOrOngoing = (festival: Festival) => {
		if (!festival.start_date || !festival.end_date) return false;
		const endDate = new Date(festival.end_date);
		endDate.setDate(endDate.getDate() + 1);
		endDate.setHours(23, 59, 59, 999);
		return endDate >= now;
	};

	switch (status) {
		case "all":
			return festivals;
		case "lineup":
			return festivals.filter((f) => isLineupOnlyFestival(f));
		case "upcoming":
			return festivals.filter(
				(f) => !isLineupOnlyFestival(f) && isUpcomingOrOngoing(f),
			);
		case "past":
			return festivals.filter(
				(f) => !isLineupOnlyFestival(f) && !isUpcomingOrOngoing(f),
			);
		default:
			return festivals;
	}
}
